import { Record as record } from 'immutable';
import joi from 'joi';
import _ from 'lodash';

import t from '../../../../src/lib/I18n';
import { construct, resolve, fields } from '../../../../src/helpers/LinkMapper';
import { getDefaults, getResolvers } from '../../../../src/helpers/Records';
import ValidationMixin from '../../../../src/records/ValidationMixin';
import validators from '../../../../src/validation';
import { toPence } from '../../../../src/helpers/Currency';
import {
    DelayRepayClaimType,
    DelayRepayClaimReason
} from '../../../../src/constants/AppConstants';

import Station from '../../../../src/records/Station';
import Route from './Route';
import NonSeasonTicketType from './NonSeasonTicketType';
import PurchaseLocation from './PurchaseLocation';

const fieldSchema = {
    ticketType: [resolve.as(NonSeasonTicketType)],
    route: [resolve.as(Route)],
    price: [fields.string],
    ticketNumber: [fields.string],
    bookSize: [fields.number, 5],
    smartcardNumber: [fields.string],
    oysterNumber: [fields.string],
    purchaseLocation: [resolve.as(PurchaseLocation)],
    purchaseLocationStation: [resolve.with(Station)],
    purchaseLocationOther: [fields.string]
};

export default class NonSeasonTicket extends record(getDefaults(fieldSchema)) {
    static fromJSON(json = {}) {
        return construct(NonSeasonTicket, json, getResolvers(fieldSchema));
    }

    getComputedValues() {
        return {
            route: this.route && this.route.isComplete(),
            routeSelected: !!this.route,
            purchaseLocationStation: this.getIn([
                'purchaseLocationStation',
                'nlc'
            ])
        };
    }

    getSchema() {
        const { ticketType } = this;

        let schema = {
            ticketType: joi
                .object()
                .required()
                .messages({ 'object.base': t('alertmessage.required') })
        };

        if (ticketType) {
            if (ticketType.stations) {
                schema.route = validators.true();
                schema.routeSelected = validators.true();
            }

            if (ticketType.ticketNumber) {
                schema.ticketNumber = validators
                    .regex(/^[\da-z-]{5}$/i)
                    .optional()
                    .allow(null, '');
            }

            if (ticketType.smartcardNumber) {
                schema.smartcardNumber = validators
                    .regex(
                        /^\d{6}[ -]?\d{4}[ -]?\d{4}[ -]?\d{4}$/,
                        t('delayrepay.label.smartcardNumber')
                    )
                    .required();
            }
            if (ticketType.oysterNumber) {
                schema.oysterNumber = validators
                    .regex(
                        /^\d{10}\s?\d{2}$/,
                        t('compensation.label.oysterNumber')
                    )
                    .required();
            }

            if (ticketType.price) {
                schema.price = joi
                    .number()
                    .min(0.01)
                    .max(200)
                    .required();
            }

            if (ticketType.purchaseLocation) {
                const { purchaseLocation } = this;
                const requiresOther =
                    purchaseLocation && purchaseLocation.requiresOther;
                const requiresStation =
                    purchaseLocation && purchaseLocation.requiresStation;
                Object.assign(schema, {
                    purchaseLocation: joi
                        .object()
                        .required()
                        .messages({ 'object.base': t('alertmessage.required') }),
                    purchaseLocationOther: requiresOther
                        ? validators.requiredString(1, 50)
                        : joi.any(),
                    purchaseLocationStation: requiresStation
                        ? validators.requiredString()
                        : joi.any()
                });
            }
        }
        return schema;
    }

    accepts(field) {
        const { ticketType } = this;
        if (!ticketType) {
            return false;
        }

        const lookups = {
            purchaseLocationStation: () =>
                this.accepts('purchaseLocation') &&
                this.getIn(['purchaseLocation', 'requiresStation']),
            purchaseLocationOther: () =>
                this.accepts('purchaseLocation') &&
                this.getIn(['purchaseLocation', 'requiresOther'])
        };

        return _.result(lookups, field, ticketType.get(field));
    }

    canPair() {
        return _.result(this, 'ticketType.canPair', false);
    }

    mustPair() {
        return _.result(this, 'ticketType.mustPair', false);
    }

    hasEligibleRoute() {
        const { route } = this;
        return this.accepts('stations') && route && route.isEligible();
    }

    getRoute() {
        return this.hasEligibleRoute() ? this.route : null;
    }

    get cardNumber() {
        const { ticketType } = this;
        let cardNumber;
        if (ticketType.smartcardNumber) {
            cardNumber = this.smartcardNumber;
        } else if (ticketType.oysterNumber) {
            cardNumber = this.oysterNumber;
        }
        if (cardNumber) {
            cardNumber = cardNumber.replace(/[\s-]+/g, '');
        }
        return cardNumber;
    }

    asClaim({ date, evidence }) {
        const { ticketType, route } = this;
        const { routeCode, origin, destination } = route || {};
        const additionalInformation = _.pickBy({
            routeCode: _.get(routeCode, 'code'),
            routeDescription: _.get(routeCode, 'description')
        });
        return {
            claimType: DelayRepayClaimType.NON_SEASON_COMPENSATION,
            ticket: {
                type: ticketType.id,
                ticketClass: null,
                number: this.accepts('ticketNumber') ? this.ticketNumber : null,
                price: this.accepts('price') ? toPence(this.price) : 0,
                photocardNumber: null,
                smartcardNumber: this.cardNumber,
                bookSize: this.accepts('bookSize') ? this.bookSize : null,
                purchaseLocation: this.accepts('purchaseLocation')
                    ? this.purchaseLocation.id
                    : null,
                purchaseStation: this.accepts('purchaseLocationStation')
                    ? this.getIn(['purchaseLocationStation', 'nlc'])
                    : null,
                purchaseOther: this.accepts('purchaseLocationOther')
                    ? this.purchaseLocationOther
                    : null,
                receiptImageId: null,
                ticketImageId: _.get(evidence, 'imageId'),
                previousUpload: Boolean(
                    _.get(evidence, 'skipFileUpload')
                ),
                evidenceType: _.get(evidence, 'type.id'),
                evidenceComments: _.get(evidence, 'type.comments')
                    ? evidence.comments
                    : null
            },
            journey: {
                originStation: this.accepts('stations') ? origin.nlc : '0000',
                destinationStation: this.accepts('stations')
                    ? destination.nlc
                    : '0000',
                changeStation: [],
                departureTime: '00:00',
                departureDate: date.format('YYYY-MM-DD'),
                scheduledArrivalDate: date.format('YYYY-MM-DD')
            },
            delay: {
                length: '120',
                reason: DelayRepayClaimReason.OTHER,
                additionalInformation: JSON.stringify(
                    additionalInformation,
                    null,
                    2
                )
            }
        };
    }
}

Object.assign(NonSeasonTicket.prototype, ValidationMixin);
